import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import PincodeInput from "@/components/common/PincodeInput";
import LeadDocumentsField from "@/components/leads/LeadDocumentsField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = { customer_name: "", property_paper_ref: "", property_address: "", location: "", valuation_type: "pre", branch: "", tat_hours: 48, notes: "", bank_id: "", city: "", pincode: "" };

function Field({ label, className = "", children }) {
  return <div className={className}><Label>{label}</Label>{children}</div>;
}

/* Create a new valuation request (admin/bank/office/field), optionally with property documents attached. */
export default function AddLeadDialog({ role, banks, onClose, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pincodeOk, setPincodeOk] = useState(false);
  const [docs, setDocs] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location || !pincodeOk) { toast.error("Please enter a valid Rajasthan pincode"); return; }
    setBusy(true);
    try {
      const payload = { ...form, document_ids: docs.map((d) => d.file_id) };
      if (role !== "bank" && !payload.bank_id) payload.bank_id = null;
      onSaved((await api.post("/leads", payload)).data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading">{t("add_lead")}</DialogTitle>
        <DialogDescription>Create a new property valuation request.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t("customer_name")} className="md:col-span-2"><Input required value={form.customer_name} onChange={set("customer_name")} data-testid="lead-customer-name" /></Field>
        <Field label={t("property_paper")}><Input value={form.property_paper_ref} onChange={set("property_paper_ref")} data-testid="lead-paper-ref" /></Field>
        <Field label={t("branch")}><Input value={form.branch} onChange={set("branch")} data-testid="lead-branch" /></Field>
        <Field label={t("property_address")} className="md:col-span-2"><Textarea required value={form.property_address} onChange={set("property_address")} data-testid="lead-address" /></Field>
        <PincodeInput value={form.pincode} label="Property Pincode" required testId="lead-pincode"
          onChange={(p) => { setForm((f) => ({ ...f, pincode: p })); setPincodeOk(false); }}
          onResolved={(r) => { setPincodeOk(r.isRajasthan); if (r.isRajasthan) setForm((f) => ({ ...f, pincode: r.pincode, city: r.city, location: r.district })); }} />
        <Field label="City / District"><Input value={form.city ? `${form.city}, ${form.location}` : ""} readOnly className="bg-slate-50" data-testid="lead-citydistrict" /></Field>
        <Field label={t("valuation_type")}>
          <Select value={form.valuation_type} onValueChange={set("valuation_type")}>
            <SelectTrigger data-testid="lead-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="pre">{t("pre_visit")}</SelectItem><SelectItem value="post">{t("post_visit")}</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label={t("tat")}><Input type="number" value={form.tat_hours} onChange={(e) => setForm((f) => ({ ...f, tat_hours: Number(e.target.value) }))} data-testid="lead-tat" /></Field>
        {role !== "bank" && (
          <Field label="Bank" className="md:col-span-2">
            <Select value={form.bank_id} onValueChange={set("bank_id")}>
              <SelectTrigger data-testid="lead-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} — {b.branch}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label={t("notes")} className="md:col-span-2"><Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="lead-notes" /></Field>
        <div className="md:col-span-2">
          <LeadDocumentsField docs={docs} onAdd={(d) => setDocs((prev) => [...prev, d])} onRemove={(id) => setDocs((prev) => prev.filter((d) => d.file_id !== id))}
            hint="Sale deed, sanction letter, approved map, ID proof… After saving you can share them by Email or WhatsApp." testId="lead-docs" />
        </div>
        <DialogFooter className="md:col-span-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="lead-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
